import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { useEmployeeNotificationsOptional } from '../../contexts/EmployeeNotificationsContext'
import { Button, FormGroup } from '../../components/ui'
import Icon from '../../components/Icon'
import { normalizeDate } from '../../utils/dateUtils'
import { formatReportPercent, formatReportRub, parseAmount } from '../../utils/formatRub'
import { REPORT_ACCENT, reportPdfButtonStyle } from './reportPreviewUi'
import './ContractReportPreview.css'
const currentYear = new Date().getFullYear()

function getActAmountAfterTax(act) {
  const afterTax = parseAmount(act?.amount_after_tax)
  return afterTax == null ? (parseAmount(act?.amount) || 0) : afterTax
}

export function amountAfterVatForReport (value, vatPercent) {
  const amount = parseAmount(value)
  if (amount == null) return null
  const vat = Number(vatPercent) || 5
  return Math.round((amount - (amount * vat / (100 + vat))) * 100) / 100
}

function getStageAmountAfterVat (stage, vatPercent) {
  const explicitAfterVat = parseAmount(stage?.cost_after_tax ?? stage?.cost_no_vat)
  if (explicitAfterVat != null) return explicitAfterVat
  return amountAfterVatForReport(stage?.cost, vatPercent) || 0
}

export function getContractIncomePercentageBase (stages, totalAmount, vatPercent) {
  const stagesAfterVat = (stages || []).reduce((sum, stage) => sum + getStageAmountAfterVat(stage, vatPercent), 0)
  if (stagesAfterVat > 0) return stagesAfterVat
  return amountAfterVatForReport(totalAmount, vatPercent) || 0
}

export function getContractIncomePercent (income, percentageBase) {
  const amount = parseAmount(income) || 0
  const base = parseAmount(percentageBase) || 0
  return base ? (amount / base) * 100 : 0
}

function formatDateDDMMYYYY (d) {
  if (d == null) return '—'
  try {
    const iso = typeof d === 'string' ? normalizeDate(d) : null
    let y, m, day
    if (iso) {
      y = parseInt(iso.slice(0, 4), 10)
      m = parseInt(iso.slice(5, 7), 10)
      day = parseInt(iso.slice(8, 10), 10)
    } else if (d && typeof d.getFullYear === 'function') {
      y = d.getFullYear()
      m = d.getMonth() + 1
      day = d.getDate()
    } else {
      return '—'
    }
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(day)) return '—'
    const dd = String(day).padStart(2, '0')
    const mm = String(m).padStart(2, '0')
    return `${dd}.${mm}.${y}`
  } catch (_) {
    return '—'
  }
}

function quarterFromDate (d) {
  if (!d) return '—'
  try {
    const str = normalizeDate(d)
    if (!str) return '—'
    const y = parseInt(str.slice(0, 4), 10)
    const m = parseInt(str.slice(5, 7), 10)
    const q = Math.ceil(m / 3)
    return `${q} кв. ${y} г.`
  } catch (_) {
    return '—'
  }
}

function findMyPendingApproval (approvals, user, budgetStatus) {
  if ((budgetStatus || '').toLowerCase().trim() !== 'в согласовании') return null
  const eid = user?.employee_id
  const name = String(user?.employee_full_name || user?.full_name || '').trim()
  const list = approvals || []
  if (eid != null && eid !== '') {
    const byId = list.find(
      (a) => (a.status || '').toLowerCase() === 'pending' && a.employee_id != null && String(a.employee_id) === String(eid)
    )
    if (byId) return byId
  }
  if (!name) return null
  return list.find((a) => (a.status || '').toLowerCase() === 'pending' && (a.employee_name || '').trim() === name) || null
}

function ContractReportPreview () {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const notifications = useEmployeeNotificationsOptional()
  const isApproveMode = searchParams.get('action') === 'approve'
  const canUpdate = user?.permissions?.['contracts.update']
  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [approveLoading, setApproveLoading] = useState(false)
  const [error, setError] = useState(null)
  const [approveError, setApproveError] = useState(null)
  const [stagesWithoutDistribution, setStagesWithoutDistribution] = useState([])
  const [approvals, setApprovals] = useState([])
  const [signerLoading, setSignerLoading] = useState(false)
  const [signerMessage, setSignerMessage] = useState({ type: '', text: '' })
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectComment, setRejectComment] = useState('')

  useEffect(() => {
    let cancelled = false
    async function fetchContract () {
      try {
        setLoading(true)
        setError(null)
        const res = await api.get(`/company/contracts/${id}`)
        if (!cancelled && res.data?.contract) setContract(res.data.contract)
      } catch (e) {
        if (!cancelled) setError(e.response?.status === 404 ? 'Договор не найден' : 'Не удалось загрузить данные')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchContract()
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    let cancelled = false
    async function fetchApprovals () {
      try {
        const res = await api.get(`/company/contracts/${id}/approvals`)
        if (!cancelled) setApprovals(res.data?.approvals || [])
      } catch (_) {
        if (!cancelled) setApprovals([])
      }
    }
    if (id) fetchApprovals()
    return () => { cancelled = true }
  }, [id])

  const downloadPdf = async () => {
    setPdfLoading(true)
    setError(null)
    try {
      const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat?.().resolvedOptions?.()?.timeZone
      const pdfEndpoint = tz ? `/company/contracts/${id}/report/pdf?tz=${encodeURIComponent(tz)}` : `/company/contracts/${id}/report/pdf`
      const response = await api.get(pdfEndpoint, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const cd = response.headers['content-disposition']
      let filename = `otchet-dogovor-${(contract?.number || id || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}-${new Date().toISOString().slice(0, 10)}.pdf`
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || cd.match(/filename=["']?([^"';]+)["']?/i)
        if (m?.[1]) filename = m[1].trim()
      }
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      const status = e.response?.status
      setError(status === 404 ? 'Договор не найден' : status === 403 ? 'Недостаточно прав' : 'Не удалось скачать PDF')
    } finally {
      setPdfLoading(false)
    }
  }

  const startApproveBudget = async () => {
    setApproveLoading(true)
    setApproveError(null)
    setStagesWithoutDistribution([])
    try {
      await api.post(`/company/contracts/${id}/approve-budget`)
      navigate(`/contract/${id}`)
    } catch (err) {
      const data = err.response?.data
      if (data?.stages_without_distribution) {
        setStagesWithoutDistribution(data.stages_without_distribution)
        setApproveError(data.error || 'Не все стадии настроены')
      } else {
        setApproveError(data?.error || 'Не удалось запустить согласование')
      }
    } finally {
      setApproveLoading(false)
    }
  }

  const submitSignerApprove = async () => {
    setSignerLoading(true)
    setSignerMessage({ type: '', text: '' })
    try {
      await api.patch(`/company/contracts/${id}/approvals/me`, { status: 'approved' })
      setSignerMessage({ type: 'success', text: 'Бюджет согласован' })
      const [res] = await Promise.all([
        api.get(`/company/contracts/${id}/approvals`),
        notifications?.refreshPendingApprovals(),
      ])
      setApprovals(res.data?.approvals || [])
      setTimeout(() => navigate(`/contract/${id}`), 1500)
    } catch (err) {
      setSignerMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить' })
    } finally {
      setSignerLoading(false)
    }
  }

  const submitSignerReject = async () => {
    setSignerLoading(true)
    setSignerMessage({ type: '', text: '' })
    try {
      await api.patch(`/company/contracts/${id}/approvals/me`, {
        status: 'rejected',
        comment: rejectComment.trim() || undefined
      })
      setRejectModalOpen(false)
      setRejectComment('')
      setSignerMessage({ type: 'success', text: 'Отклонение сохранено' })
      const [res] = await Promise.all([
        api.get(`/company/contracts/${id}/approvals`),
        notifications?.refreshPendingApprovals(),
      ])
      setApprovals(res.data?.approvals || [])
      setTimeout(() => navigate(`/contract/${id}`), 1500)
    } catch (err) {
      setSignerMessage({ type: 'error', text: err.response?.data?.error || 'Не удалось сохранить' })
    } finally {
      setSignerLoading(false)
    }
  }

  // Структура и порядок полей должны совпадать с бэкендом (generate_contract_budget_csv). При изменении формата отчёта править оба места.
  const downloadCsv = () => {
    if (!contract) return
    const stages = contract.stages || []
    const acts = contract.acts || []
    const stagesIncome = contract.stages_income || []
    const clientName = (contract.client_name ?? '').trim() || '—'
    const objectName = (contract.object_name ?? '').trim() || '—'
    const contractNumber = (contract.number ?? '').trim() || '—'
    const actuatedByStage = {}
    acts.forEach(a => {
      const sid = a.stage_id != null ? String(a.stage_id) : ''
      if (!sid) return
      const d = a.issue_date
      let y = currentYear
      if (d) {
        if (typeof d === 'string' && d.length >= 4) y = parseInt(d.slice(0, 4), 10)
        else if (typeof d === 'object' && d != null && 'getFullYear' in d) y = d.getFullYear()
      }
      if (y === currentYear) {
        const amt = getActAmountAfterTax(a)
        actuatedByStage[sid] = (actuatedByStage[sid] || 0) + amt
      }
    })
    const totalAmount = contract.total_amount != null ? Number(contract.total_amount) : null
    const vatPct = Number(contract.vat_percent) || 5
    const csvNum = (value) => {
      if (value == null || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : '—'
    }
    const csvPct = (value) => {
      const n = Number(value)
      return (Number.isFinite(n) ? n : 0).toFixed(2).replace('.', ',') + '%'
    }
    const sumAfterTax = amountAfterVatForReport(totalAmount, vatPct)
    const totalCostBefore = stages.reduce((s, st) => s + (Number(st.cost ?? 0) || 0), 0)
    const incomePercentBase = getContractIncomePercentageBase(stages, totalAmount, vatPct)
    const totalActThis = stages.reduce((s, st) => s + (Number(st.actuated_in_year ?? actuatedByStage[String(st.id ?? st.type ?? '')] ?? 0) || 0), 0)
    const totalPrev = stages.reduce((s, st) => s + (Number(st.prev_actuated_calculated ?? st.prev_actuated_amount ?? 0) || 0), 0)
    const totalRemainder = Math.max(0, totalCostBefore - totalActThis - totalPrev)

    const escapeCsv = (cells) => cells.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')
    const allRows = []

    // Блок 1: Сводка по договору
    allRows.push([`Сводка по договору № ${contractNumber} (${objectName}, ${clientName})`])
    allRows.push(['Сумма договора до НДС', 'Сумма договора после НДС', 'Тип стадии', 'Сумма стадии до НДС', 'Сумма стадии после НДС', 'Заактировано в году', 'Заактировано ранее', 'Незаактированный остаток'])
    if (stages.length === 0) {
      allRows.push([csvNum(totalAmount), csvNum(sumAfterTax), '—', '—', '—', '—', '—', '—'])
    } else {
      stages.forEach((stage, i) => {
        const sid = String(stage.id ?? stage.type ?? '')
        const costBefore = Number(stage.cost ?? 0) || 0
        const costAfter = getStageAmountAfterVat(stage, vatPct)
        const prev = Number(stage.prev_actuated_calculated ?? stage.prev_actuated_amount ?? 0) || 0
        const actThis = Number(stage.actuated_in_year ?? actuatedByStage[sid] ?? 0) || 0
        const remainder = Math.max(0, costBefore - actThis - prev)
        allRows.push([
          i === 0 && totalAmount != null ? csvNum(totalAmount) : '',
          i === 0 && sumAfterTax != null ? csvNum(sumAfterTax) : '',
          (stage.type ?? stage.id ?? '—'),
          costBefore ? csvNum(costBefore) : '—',
          costAfter ? csvNum(costAfter) : '—',
          csvNum(actThis),
          csvNum(prev),
          csvNum(remainder)
        ])
      })
      allRows.push([csvNum(totalAmount), csvNum(sumAfterTax), 'ИТОГО:', csvNum(totalCostBefore), csvNum(sumAfterTax), csvNum(totalActThis), csvNum(totalPrev), csvNum(totalRemainder)])
    }
    allRows.push([])

    // Блок 2: Детализация по стадиям
    stagesIncome.forEach(block => {
      const stageName = String(block.stage_name ?? block.stage_id ?? '—').slice(0, 80)
      allRows.push([`Детализация по стадии: ${stageName}`])
      allRows.push(['Доходы исполнителей'])
      allRows.push(['Подразделение', 'Исполнитель', 'Сумма выплат (до НДС)', 'Общий %'])
      const stageRows = (block.departments || []).flatMap(dept =>
        (dept.teams || []).flatMap(team =>
          (team.employees || []).map(emp => ({
            dept: dept.department_name ?? '—',
            name: emp.employee_name ?? '—',
            inc: Number(emp.income ?? 0) || 0
          }))
        )
      )
      stageRows.forEach(r => {
        const pct = getContractIncomePercent(r.inc, incomePercentBase)
        allRows.push([r.dept, r.name, csvNum(r.inc), csvPct(pct)])
      })
      const stageTotal = stageRows.reduce((s, r) => s + r.inc, 0)
      if (stageTotal > 0) {
        const pct = getContractIncomePercent(stageTotal, incomePercentBase)
        allRows.push(['ОБЩАЯ СУММА', '', csvNum(stageTotal), csvPct(pct)])
      }
      const otherExpenses = block.other_expenses || []
      if (otherExpenses.length > 0) {
        allRows.push([])
        allRows.push(['Другие расходы'])
        allRows.push(['Тип расходов', 'Сумма (до НДС)', 'Общий %'])
        let otherTotalAmount = 0
        let otherTotalPct = 0
        otherExpenses.forEach(r => {
          const amt = Number(r.amount) || 0
          const pct = Number(r.percentage) || 0
          otherTotalAmount += amt
          otherTotalPct += pct
          allRows.push([r.expense_type ?? '—', csvNum(r.amount), csvPct(pct)])
        })
        allRows.push(['ОБЩАЯ СУММА', csvNum(otherTotalAmount), csvPct(otherTotalPct)])
      }
      allRows.push([])
    })

    // Блок 3: Акты по договору
    allRows.push(['Акты по договору'])
    allRows.push(['Рабочее название объекта', 'Заказчик', 'Договор', 'ID стадии', 'Дата', 'Сумма (до НДС)', 'Период'])
    if (acts.length === 0) {
      allRows.push(['—', '—', '—', 'Акты отсутствуют', '—', '—', '—'])
    } else {
      acts.forEach(a => {
        const period = (a.period && String(a.period).trim()) || quarterFromDate(a.issue_date)
        const dateStr = formatDateDDMMYYYY(a.issue_date)
        const amount = getActAmountAfterTax(a)
        allRows.push([
          objectName,
          clientName,
          contractNumber,
          a.stage_name ?? a.stage_id ?? '—',
          dateStr,
          csvNum(amount),
          period || '—'
        ])
      })
    }

    const csvContent = allRows.map(r => escapeCsv(r)).join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `otchet-dogovor-${(contract.number || id || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const genDate = new Date()
  const dateStr = genDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = genDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

  if (loading) {
    return (
      <div className="contract-report-preview contract-report-preview--loading">
        Загрузка…
      </div>
    )
  }
  if (error || !contract) {
    return (
      <div className="contract-report-preview contract-report-preview--error">
        <p>{error || 'Договор не найден'}</p>
        <Button variant="ghost" className="contract-report-preview__btn-back" onClick={() => navigate(`/contract/${id}`)}>← Назад к договору</Button>
      </div>
    )
  }

  const stages = contract.stages || []
  const stagesIncome = contract.stages_income || []
  const acts = contract.acts || []
  const actuatedByStage = {}
  acts.forEach(a => {
    const sid = a.stage_id != null ? String(a.stage_id) : ''
    if (!sid) return
    const d = a.issue_date
    let y = currentYear
    if (d) {
      if (typeof d === 'string' && d.length >= 4) y = parseInt(d.slice(0, 4), 10)
      else if (typeof d === 'object' && d != null && 'getFullYear' in d) y = d.getFullYear()
    }
    if (y === currentYear) {
      const amt = getActAmountAfterTax(a)
      actuatedByStage[sid] = (actuatedByStage[sid] || 0) + amt
    }
  })
  const totalAmountNum = contract.total_amount != null ? Number(contract.total_amount) : null
  const vatPercent = Number(contract.vat_percent) || 5
  const sumAfterTax = amountAfterVatForReport(totalAmountNum, vatPercent)
  const totalActThis = stages.reduce((s, st) => s + (Number(st.actuated_in_year ?? actuatedByStage[String(st.id ?? st.type ?? '')] ?? 0) || 0), 0)
  const totalPrev = stages.reduce((s, st) => s + (Number(st.prev_actuated_calculated ?? st.prev_actuated_amount ?? 0) || 0), 0)
  const totalCostBefore = stages.reduce((s, st) => s + (Number(st.cost ?? 0) || 0), 0)
  const incomePercentBase = getContractIncomePercentageBase(stages, totalAmountNum, vatPercent)
  const totalRemainder = Math.max(0, totalCostBefore - totalActThis - totalPrev)
  const clientName = (contract.client_name ?? '').trim() || '—'
  const objectName = (contract.object_name ?? '').trim() || '—'
  const contractNumber = (contract.number ?? '').trim() || '—'
  const myPendingApproval = findMyPendingApproval(approvals, user, contract.budget_status)
  const showSignerActions = Boolean(myPendingApproval)

  return (
    <div className="contract-report-preview">
      <div className="contract-report-preview__toolbar">
        <h1 className="contract-report-preview__toolbar-title">Предпросмотр отчёта</h1>
        <div className="contract-report-preview__toolbar-actions">
          <Button variant="ghost" onClick={() => navigate(`/contract/${id}`)} className="contract-report-preview__btn-back">
            ← Назад к договору
          </Button>
          <Button variant="secondary" onClick={downloadCsv} disabled={pdfLoading}>
            <Icon name="download" size={18} /> Скачать CSV
          </Button>
          <Button variant="primary" onClick={downloadPdf} disabled={pdfLoading} style={reportPdfButtonStyle}>
            <Icon name="download" size={18} /> {pdfLoading ? 'Формируем…' : 'Скачать PDF'}
          </Button>
          {showSignerActions && (
            <>
              <Button
                variant="primary"
                onClick={submitSignerApprove}
                disabled={signerLoading}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', ...reportPdfButtonStyle }}
              >
                <Icon name="check" size={18} />
                {signerLoading ? 'Сохранение…' : 'Согласовать'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setRejectComment(''); setRejectModalOpen(true) }}
                disabled={signerLoading}
              >
                <Icon name="close" size={18} />
                Отклонить
              </Button>
            </>
          )}
          {isApproveMode && canUpdate && (
            <Button
              variant="primary"
              onClick={startApproveBudget}
              disabled={approveLoading}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', ...reportPdfButtonStyle }}
            >
              <Icon name="check" size={18} />
              {approveLoading ? 'Отправка…' : 'Согласовать бюджет'}
            </Button>
          )}
        </div>
      </div>

      {signerMessage.text && (
        <div className={`contract-report-preview__message ${signerMessage.type === 'error' ? 'contract-report-preview__message--error' : ''}`} style={signerMessage.type === 'success' ? { background: 'var(--color-secondary-bg)', color: 'var(--color-text)' } : undefined} role="alert">
          {signerMessage.text}
        </div>
      )}

      {error && <div className="contract-report-preview__message contract-report-preview__message--error">{error}</div>}

      {approveError && (
        <div className="contract-report-preview__message contract-report-preview__message--error">
          <div>{approveError}</div>
          {stagesWithoutDistribution.length > 0 && (
            <ul style={{ margin: 'var(--spacing-sm) 0 0', paddingLeft: 'var(--spacing-lg)' }}>
              {stagesWithoutDistribution.map((s) => (
                <li key={s.id} style={{ marginBottom: 'var(--spacing-xs)' }}>
                  <span>{s.display_name || s.type || s.id}</span>
                  {' — '}
                  <a
                    href={`/company/contracts/${id}/stages/${encodeURIComponent(s.id)}/edit`}
                    onClick={(e) => { e.preventDefault(); navigate(`/company/contracts/${id}/stages/${encodeURIComponent(s.id)}/edit`) }}
                    style={{ color: 'var(--color-primary)' }}
                  >
                    настроить
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="contract-report-preview__page pdf-page">
        <header className="contract-report-preview__header">
          <div className="contract-report-preview__header-left">
            <div className="contract-report-preview__logo">
              <img src="/logo/logo.png" alt="2ПБ" onError={e => { e.target.onerror = null; e.target.src = '/logo/logo.jpg' }} />
            </div>
            <div>
              <h2 className="contract-report-preview__title">ПРОЕКТНАЯ АРТЕЛЬ</h2>
              <p className="contract-report-preview__subtitle">Бюджет по договору · Внутренний отчёт</p>
            </div>
          </div>
          <div className="contract-report-preview__header-right">
            <p className="contract-report-preview__date-label">Дата формирования</p>
            <p className="contract-report-preview__date">{dateStr} <span className="contract-report-preview__date-sep">|</span> {timeStr}</p>
          </div>
        </header>

        <section className="contract-report-preview__section">
          <div className="contract-report-preview__section-head">
            <div className="contract-report-preview__accent-bar" style={{ background: REPORT_ACCENT }} />
            <h3 className="contract-report-preview__section-title">
              Сводка по договору № {contractNumber} ({objectName}, {clientName})
            </h3>
          </div>
          <div className="contract-report-preview__table-wrap">
            <table className="contract-report-preview__table">
              <thead>
                <tr>
                  <th>Сумма дог. до НДС</th>
                  <th>Сумма дог. после НДС</th>
                  <th>Тип стадии</th>
                  <th>Сумма стадии до НДС</th>
                  <th>Сумма стадии после НДС</th>
                  <th>Заактировано в году</th>
                  <th>Заактировано ранее</th>
                  <th>Остаток</th>
                </tr>
              </thead>
              <tbody>
                {stages.length === 0 ? (
                  <tr>
                    <td className="contract-report-preview__num">{formatReportRub(totalAmountNum)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(sumAfterTax)}</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                    <td>—</td>
                  </tr>
                ) : stages.map((stage, i) => {
                  const sid = String(stage.id ?? stage.type ?? '')
                  const costBefore = Number(stage.cost ?? 0) || 0
                  const costAfter = getStageAmountAfterVat(stage, vatPercent)
                  const prev = Number(stage.prev_actuated_calculated ?? stage.prev_actuated_amount ?? 0) || 0
                  const actThis = Number(stage.actuated_in_year ?? actuatedByStage[sid] ?? 0) || 0
                  const remainder = Math.max(0, costBefore - actThis - prev)
                  return (
                    <tr key={stage.id ?? i}>
                      <td className="contract-report-preview__num">{i === 0 && totalAmountNum != null ? formatReportRub(totalAmountNum) : ''}</td>
                      <td className="contract-report-preview__num">{i === 0 && sumAfterTax != null ? formatReportRub(sumAfterTax) : ''}</td>
                      <td>{stage.type ?? stage.id ?? '—'}</td>
                      <td className="contract-report-preview__num">{costBefore ? formatReportRub(costBefore) : '—'}</td>
                      <td className="contract-report-preview__num">{costAfter ? formatReportRub(costAfter) : '—'}</td>
                      <td className="contract-report-preview__num">{formatReportRub(actThis)}</td>
                      <td className="contract-report-preview__num">{formatReportRub(prev)}</td>
                      <td className="contract-report-preview__num contract-report-preview__num--remainder">{formatReportRub(remainder)}</td>
                    </tr>
                  )
                })}
                {stages.length > 0 && (
                  <tr className="contract-report-preview__total-row">
                    <td className="contract-report-preview__num">{formatReportRub(totalAmountNum)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(sumAfterTax)}</td>
                    <td className="contract-report-preview__total-label">ИТОГО:</td>
                    <td className="contract-report-preview__num">{formatReportRub(totalCostBefore)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(sumAfterTax)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(totalActThis)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(totalPrev)}</td>
                    <td className="contract-report-preview__num contract-report-preview__num--accent">{formatReportRub(totalRemainder)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {stagesIncome.length > 0 && stagesIncome.map((block, bi) => {
          const stageRows = (block.departments || []).flatMap(dept =>
            (dept.teams || []).flatMap(team =>
              (team.employees || []).map(emp => ({
                dept: dept.department_name ?? '—',
                name: emp.employee_name ?? '—',
                inc: Number(emp.income ?? 0) || 0
              }))
            )
          )
          const stageTotal = stageRows.reduce((s, r) => s + r.inc, 0)
          const stagePct = formatReportPercent(getContractIncomePercent(stageTotal, incomePercentBase))
          return (
            <section key={bi} className="contract-report-preview__section">
              <div className="contract-report-preview__detail-head" style={{ borderLeftColor: REPORT_ACCENT }}>
                <h3 className="contract-report-preview__detail-title">Детализация по стадии</h3>
                <p className="contract-report-preview__detail-stage">{String(block.distribution_type || block.stage_type || block.stage_name || block.stage_id || '').slice(0, 80)}</p>
              </div>
              <h4 className="contract-report-preview__subsection-title">Доходы исполнителей</h4>
              <div className="contract-report-preview__table-wrap">
                <table className="contract-report-preview__table contract-report-preview__table--detail">
                  <colgroup>
                    <col style={{ width: '27.32%' }} />
                    <col style={{ width: '32.79%' }} />
                    <col style={{ width: '24.59%' }} />
                    <col style={{ width: '15.30%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Подразделение</th>
                      <th>Исполнитель</th>
                      <th>Сумма выплат (до НДС)</th>
                      <th>Общий %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stageRows.map((row, ei) => {
                      const pct = formatReportPercent(getContractIncomePercent(row.inc, incomePercentBase))
                      return (
                        <tr key={ei}>
                          <td className="contract-report-preview__dept">{row.dept}</td>
                          <td>{row.name}</td>
                          <td className="contract-report-preview__num">{formatReportRub(row.inc)}</td>
                          <td className="contract-report-preview__num contract-report-preview__num--pct">{pct}</td>
                        </tr>
                      )
                    })}
                    {stageRows.length > 0 && (
                      <tr className="contract-report-preview__total-row">
                        <td colSpan={2} className="contract-report-preview__total-label">ОБЩАЯ СУММА</td>
                        <td className="contract-report-preview__num">{formatReportRub(stageTotal)}</td>
                        <td className="contract-report-preview__num contract-report-preview__num--pct">{stagePct}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(block.other_expenses && block.other_expenses.length > 0) && (() => {
                const oe = block.other_expenses
                const oeTotalAmount = oe.reduce((s, r) => s + (Number(r.amount) || 0), 0)
                const oeTotalPct = oe.reduce((s, r) => s + (Number(r.percentage) || 0), 0)
                return (
                  <>
                    <h4 className="contract-report-preview__subsection-title">Другие расходы</h4>
                    <div className="contract-report-preview__table-wrap">
                      <table className="contract-report-preview__table contract-report-preview__table--detail contract-report-preview__table--other-expenses">
                        <colgroup>
                          <col style={{ width: '60.11%' }} />
                          <col style={{ width: '24.59%' }} />
                          <col style={{ width: '15.30%' }} />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Тип расходов</th>
                            <th>Сумма (до НДС)</th>
                            <th>Общий %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oe.map((row, ri) => (
                            <tr key={ri}>
                              <td>{row.expense_type ?? '—'}</td>
                              <td className="contract-report-preview__num">{formatReportRub(row.amount)}</td>
                              <td className="contract-report-preview__num contract-report-preview__num--pct">{formatReportPercent(row.percentage || 0)}</td>
                            </tr>
                          ))}
                          <tr className="contract-report-preview__total-row">
                            <td className="contract-report-preview__total-label">ОБЩАЯ СУММА</td>
                            <td className="contract-report-preview__num">{formatReportRub(oeTotalAmount)}</td>
                            <td className="contract-report-preview__num contract-report-preview__num--pct">{formatReportPercent(oeTotalPct)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              })()}
            </section>
          )
        })}

        <section className="contract-report-preview__section">
          <div className="contract-report-preview__section-head">
            <div className="contract-report-preview__accent-bar" style={{ background: REPORT_ACCENT }} />
            <h3 className="contract-report-preview__section-title">Акты по договору</h3>
          </div>
          <div className="contract-report-preview__table-wrap">
            <table className="contract-report-preview__table contract-report-preview__table--acts">
              <thead>
                <tr>
                  <th>Рабочее название объекта</th>
                  <th>Заказчик</th>
                  <th>Договор</th>
                  <th>ID стадии</th>
                  <th>Дата</th>
                  <th>Сумма (до НДС)</th>
                  <th>Период</th>
                </tr>
              </thead>
              <tbody>
                {(!contract.acts || contract.acts.length === 0) ? (
                  <tr>
                    <td colSpan={7}>Акты отсутствуют</td>
                  </tr>
                ) : contract.acts.map((a, ai) => {
                  const dateStr = formatDateDDMMYYYY(a.issue_date)
                  const period = (a.period && String(a.period).trim()) || quarterFromDate(a.issue_date)
                  const amount = getActAmountAfterTax(a)
                  return (
                    <tr key={a.id ?? ai}>
                      <td>{objectName}</td>
                      <td>{clientName}</td>
                      <td>{contractNumber}</td>
                      <td>{a.stage_name ?? a.stage_id ?? '—'}</td>
                      <td>{dateStr}</td>
                      <td className="contract-report-preview__num">{formatReportRub(amount)}</td>
                      <td>{period || '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="contract-report-preview__footer">
          <div>Внутренний отчёт © {currentYear}</div>
          <div>Страница 1</div>
        </footer>
      </div>

      {rejectModalOpen && (
        <div className="modal-overlay" role="presentation" onClick={() => !signerLoading && setRejectModalOpen(false)}>
          <div className="modal-content" role="dialog" aria-labelledby="contract-report-reject-title" onClick={(e) => e.stopPropagation()}>
            <h3 id="contract-report-reject-title">Отклонить согласование</h3>
            <FormGroup label="Комментарий (причина отклонения)">
              <textarea
                className="ui-input"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Причина отклонения"
                rows={3}
              />
            </FormGroup>
            <div className="modal-actions">
              <Button variant="secondary" type="button" onClick={() => setRejectModalOpen(false)} disabled={signerLoading}>Отмена</Button>
              <Button variant="primary" type="button" onClick={submitSignerReject} disabled={signerLoading}>Сохранить</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ContractReportPreview
